import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import OutlinedInput, { type OutlinedInputProps } from '@mui/material/OutlinedInput';
import { forwardRef, useId, type ReactNode } from 'react';
import { mergeSx } from '../sx';
import { Icon } from './Icon';

export interface FormTextFieldProps extends Omit<OutlinedInputProps, 'error'> {
  label: ReactNode;
  error?: string | undefined;
  helperText?: ReactNode;
  /** Field height: 40 (back-office), 48 (login), 56 (mobile). */
  height?: 40 | 48 | 56;
}

/** Label-above text field used across the design's real screens (12px/600 label, error line with icon). */
export const FormTextField = forwardRef<HTMLInputElement, FormTextFieldProps>(function FormTextField(
  { label, error, helperText, height = 40, id, fullWidth = true, sx, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const helpId = `${inputId}-help`;
  const hasHelp = Boolean(error ?? helperText);
  return (
    <FormControl fullWidth={fullWidth} error={Boolean(error)} sx={{ gap: 0.5 }}>
      <FormLabel htmlFor={inputId} sx={height === 56 ? { fontSize: 13 } : undefined}>
        {label}
      </FormLabel>
      <OutlinedInput
        id={inputId}
        inputRef={ref}
        aria-describedby={hasHelp ? helpId : undefined}
        sx={mergeSx(
          {
            '& .MuiOutlinedInput-input': {
              height: height - 20,
              py: '10px',
              fontSize: height === 40 ? 14 : height === 48 ? 15 : 16,
            },
          },
          sx,
        )}
        {...rest}
      />
      {hasHelp && (
        <FormHelperText id={helpId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, m: 0 }}>
          {error && <Icon name="error" size={16} />}
          {error ?? helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});
